import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Aviso,
  Botao,
  Campo,
  CampoDeSelecao,
  CartaoIndicador,
  EstadoCarregando,
  EstadoErro,
  EstadoVazio,
  Selo,
  Tabela,
  formatarMoeda,
  useDialogo,
  type ColunaTabela,
  type DirecaoDeOrdenacao,
} from '../../../componentes/ui';
import { AvisoDoLote, BotaoDoLote, useAcaoEmLote } from '../../../componentes/AcaoEmLote';
import { formatarCnpj, lerValorDigitado } from '../../../servicos/formato';
import { resumirNomes } from '../../../servicos/lote';
import {
  CHAVE_DA_PROSPECCAO,
  MODALIDADES,
  carregarEmpresasDaColeta,
  creditoNoFiltro,
  excluirColeta,
  exportarColeta,
  levarAoCrm,
  listarColetas,
  rotuloDoMes,
  totalDaModalidade,
  type ColetaResumo,
  type EmpresaProspectada,
  type Modalidade,
} from '../../../servicos/prospeccao';
import { Pagina } from '../../Carteira';
import { ErroDaApi } from '../../ErroDaApi';
import { AnotacoesDaEmpresa } from './AnotacoesDaEmpresa';
import { FILTRO_VAZIO, FiltroDaProspeccao, type EscolhasDoFiltro } from './FiltroDaProspeccao';
import { ImportarColeta } from './ImportarColeta';
import { RoboDoSiscred } from './RoboDoSiscred';

/** `AAAA-MM` — o mês na URL. O id da coleta muda quando ela é substituída; o mês, não. */
const chaveDoMes = (c: Pick<ColetaResumo, 'ano' | 'mes'>): string =>
  `${c.ano}-${String(c.mes).padStart(2, '0')}`;

type Ordenacao = { readonly chave: string; readonly direcao: DirecaoDeOrdenacao };

type ResultadoDoCard =
  | {
      readonly razaoSocial: string;
      readonly cardId: string;
      readonly empresaCadastrada: boolean;
      readonly cardJaExistia: boolean;
      readonly contatosLevados: number;
    }
  | { readonly razaoSocial: string; readonly erro: unknown };

/**
 * CRM → Prospecção: as empresas com crédito de ICMS disponível no SISCRED,
 * mês a mês. **Solução em teste** — isolada nesta pasta, no módulo
 * `prospeccao` da API e nas tabelas `prospeccao_*`.
 *
 * ## De onde vem a lista
 *
 * Do robô do SISCRED, que roda no servidor do Deca: sozinho no dia 10 (ou no
 * próximo dia útil), se a coleta automática estiver ligada, ou pelo "Coletar
 * agora" do quadro do robô. A planilha "Mailing extrato SISCRED" da automação
 * antiga continua importável ("Importar planilha") — a tela é a mesma para as
 * duas origens.
 *
 * ## O que é do mês e o que é da empresa
 *
 * Saldos, telefone e e-mail do extrato são daquele mês. Contatos, deságio e
 * atualizações são anotações da empresa: valem em todos os meses, como a
 * planilha fazia copiando as colunas de um mês para o outro.
 *
 * ## Um "Filtro" só, com várias opções
 *
 * Novas no mês, fora do CRM, tipo de crédito (Exportação, Diferimento,
 * Investimento) e "Créditos acima de:", marcáveis juntos (`FiltroDaProspeccao`).
 * Com tipos de crédito marcados, o "acima de", a ordenação e o valor em
 * destaque passam a ser a soma desses saldos: quem procura Exportação não quer
 * ver em primeiro a empresa grande por causa do Investimento.
 *
 * ## A lista inteira sai no xlsx
 *
 * "Extrair xlsx" leva a coleta toda, no layout da planilha de sempre, e não a
 * filtragem da tela — uma planilha que muda conforme o que está digitado na
 * busca ninguém consegue reproduzir depois (a mesma regra da carteira).
 */
export function PaginaDeProspeccao(): JSX.Element {
  const queryClient = useQueryClient();
  const dialogo = useDialogo();
  const [parametros, setParametros] = useSearchParams();

  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<EscolhasDoFiltro>(FILTRO_VAZIO);
  const minimo = filtro.acimaDe ? filtro.valor : '';
  const [aplicados, setAplicados] = useState({ busca: '', minimo: '' });
  const [ordenacao, setOrdenacao] = useState<Ordenacao>({ chave: 'total', direcao: 'decrescente' });
  const [importando, setImportando] = useState(false);
  const [abertaId, setAbertaId] = useState<string | null>(null);
  const [resultadoDoCard, setResultadoDoCard] = useState<ResultadoDoCard | null>(null);

  // Busca e valor mínimo esperam a digitação parar: sem isso, "industria"
  // dispararia nove consultas, com respostas fora de ordem.
  useEffect(() => {
    const t = setTimeout(() => setAplicados({ busca, minimo }), 350);
    return () => clearTimeout(t);
  }, [busca, minimo]);

  const coletas = useQuery({
    queryKey: [...CHAVE_DA_PROSPECCAO, 'coletas'],
    queryFn: listarColetas,
  });
  const mesNaUrl = parametros.get('mes');
  const todasAsColetas = coletas.data ?? [];
  const coleta = todasAsColetas.find((c) => chaveDoMes(c) === mesNaUrl) ?? todasAsColetas[0];
  const indiceDaColeta = coleta === undefined ? -1 : todasAsColetas.indexOf(coleta);
  const temMesAnterior = indiceDaColeta >= 0 && indiceDaColeta < todasAsColetas.length - 1;

  const valorMinimo = lerValorDigitado(aplicados.minimo);
  const { novas, foraDoCrm, modalidades } = filtro;
  const lista = useQuery({
    queryKey: [
      ...CHAVE_DA_PROSPECCAO,
      'empresas',
      coleta?.id,
      aplicados.busca,
      valorMinimo,
      novas,
      foraDoCrm,
      modalidades,
    ],
    queryFn: () =>
      carregarEmpresasDaColeta(coleta?.id ?? '', {
        busca: aplicados.busca,
        ...(valorMinimo === null ? {} : { minimo: valorMinimo }),
        novas,
        foraDoCrm,
        modalidades,
      }),
    enabled: coleta !== undefined,
    // Filtrando o mesmo mês, a lista anterior fica à vista até a nova chegar;
    // trocando de mês, não — mostrar julho com "agosto" selecionado engana.
    placeholderData: (anterior) => (anterior?.coleta.id === coleta?.id ? anterior : undefined),
  });

  const itens = useMemo(
    () => ordenar(lista.data?.itens ?? [], ordenacao, modalidades),
    [lista.data, ordenacao, modalidades],
  );
  const aberta =
    abertaId === null ? null : (lista.data?.itens.find((e) => e.id === abertaId) ?? null);

  const levar = useMutation({
    mutationFn: (e: EmpresaProspectada) => levarAoCrm(e.id),
    onSuccess: (r, e) => {
      setResultadoDoCard({ razaoSocial: e.razaoSocial, ...r });
      // O pipeline, esta lista e — se a empresa nasceu agora — o cadastro.
      void queryClient.invalidateQueries({ queryKey: ['crm'] });
      if (r.empresaCadastrada) void queryClient.invalidateQueries({ queryKey: ['empresas'] });
    },
    onError: (erro, e) => setResultadoDoCard({ razaoSocial: e.razaoSocial, erro }),
  });

  // O mesmo "Criar card" da linha, para as empresas marcadas.
  const levarMarcadas = useAcaoEmLote<EmpresaProspectada>({
    acao: (e) => levarAoCrm(e.id),
    nome: (e) => e.razaoSocial,
    aoTerminar: () => {
      setResultadoDoCard(null);
      // O pipeline e esta lista (`['crm', …]`), e o cadastro das que nasceram agora.
      void queryClient.invalidateQueries({ queryKey: ['crm'] });
      void queryClient.invalidateQueries({ queryKey: ['empresas'] });
    },
  });

  const apagar = useMutation({
    mutationFn: (c: ColetaResumo) => excluirColeta(c.id),
    onSuccess: () => {
      setParametros({}, { replace: true });
      void queryClient.invalidateQueries({ queryKey: CHAVE_DA_PROSPECCAO });
    },
  });

  const escolherMes = (chave: string): void => {
    setResultadoDoCard(null);
    setParametros({ mes: chave }, { replace: true });
  };

  // Estável: o quadro do robô a chama quando uma rodada que ele acompanhava termina.
  const irParaAColetaDoRobo = useCallback(
    (e: { readonly ano: number; readonly mes: number }) => {
      setResultadoDoCard(null);
      setParametros({ mes: chaveDoMes(e) }, { replace: true });
    },
    [setParametros],
  );

  const criarCard = (e: EmpresaProspectada): void => {
    setResultadoDoCard(null);
    levar.mutate(e);
  };

  const semPropagar = (ev: MouseEvent): void => ev.stopPropagation();

  const colunas: ColunaTabela<EmpresaProspectada>[] = [
    {
      chave: 'empresa',
      titulo: 'Empresa',
      larguraMinima: '15rem',
      ordenavel: true,
      celula: (e) => (
        <span className="flex flex-col gap-1">
          <span className="font-medium text-conteudo-primario">{e.razaoSocial}</span>
          <span className="text-xs tabular-nums text-conteudo-sutil">{formatarCnpj(e.cnpj)}</span>
          {(e.nova || e.cadastro !== null) && (
            <span className="flex flex-wrap gap-1.5">
              {e.nova && (
                <Selo tom="informativo" tamanho="pequeno">
                  Nova no mês
                </Selo>
              )}
              {e.cadastro?.cliente === true && (
                <Selo tom="positivo" tamanho="pequeno">
                  Cliente
                </Selo>
              )}
              {e.cadastro !== null && !e.cadastro.ativa && (
                <Selo tom="atencao" tamanho="pequeno">
                  Desativada no cadastro
                </Selo>
              )}
            </span>
          )}
        </span>
      ),
    },
    {
      chave: 'total',
      titulo: 'Crédito',
      numerica: true,
      ordenavel: true,
      larguraMinima: '14rem',
      celula: (e) => (
        <CreditoDaEmpresa
          empresa={e}
          {...(modalidades.length > 0 ? { noFiltro: modalidades } : {})}
        />
      ),
    },
    {
      chave: 'contato',
      titulo: 'Contato',
      larguraMinima: '16rem',
      celula: (e) => <ResumoDoContato empresa={e} />,
    },
    {
      chave: 'crm',
      titulo: 'CRM',
      larguraMinima: '7.5rem',
      celula: (e) =>
        e.card === null ? (
          <Botao
            variante="secundario"
            tamanho="pequeno"
            carregando={levar.isPending && levar.variables?.id === e.id}
            disabled={levar.isPending}
            onClick={(ev) => {
              ev.stopPropagation();
              criarCard(e);
            }}
          >
            Criar card
          </Botao>
        ) : (
          <Link
            to={`/crm/pipeline?card=${e.card.id}`}
            onClick={semPropagar}
            className="flex flex-col text-sm text-conteudo-primario underline-offset-2 hover:underline"
          >
            Abrir card
            <span className="text-xs text-conteudo-sutil">{e.card.colunaNome}</span>
          </Link>
        ),
    },
  ];

  const acoes =
    coleta === undefined ? (
      <Botao onClick={() => setImportando(true)}>Importar planilha</Botao>
    ) : (
      <div className="flex flex-wrap items-center gap-3">
        <Botao variante="secundario" onClick={() => void exportarColeta(coleta)}>
          Extrair xlsx
        </Botao>
        <Botao onClick={() => setImportando(true)}>Importar planilha</Botao>
      </div>
    );

  return (
    <Pagina
      titulo="Prospecção"
      descricao="Empresas com crédito de ICMS disponível no SISCRED, mês a mês. Anote os contatos e leve ao pipeline quem interessar."
      {...(lista.data === undefined ? {} : { contagem: lista.data.resumo.empresas })}
      acao={acoes}
    >
      <div className="flex flex-col gap-6">
        <RoboDoSiscred coletas={todasAsColetas} aoConcluir={irParaAColetaDoRobo} />
        {coletas.isError ? (
          <EstadoErro
            titulo="Não foi possível carregar as coletas"
            mensagem={coletas.error.message}
            aoTentarNovamente={() => void coletas.refetch()}
          />
        ) : coletas.data === undefined ? (
          <EstadoCarregando contexto="as coletas" />
        ) : coleta === undefined ? (
          <EstadoVazio
            comMoldura
            titulo="Nenhuma coleta ainda"
            descricao='Use "Coletar agora", no quadro do robô, ou suba a planilha "Mailing extrato SISCRED". O Deca guarda a lista do mês e as anotações de cada empresa.'
            acao={<Botao onClick={() => setImportando(true)}>Importar planilha</Botao>}
          />
        ) : (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
              <CampoDeSelecao
                rotulo="Mês da coleta"
                className="lg:w-80"
                value={chaveDoMes(coleta)}
                onChange={(ev) => escolherMes(ev.target.value)}
                opcoes={todasAsColetas.map((c) => ({
                  valor: chaveDoMes(c),
                  rotulo: `${rotuloDoMes(c.ano, c.mes)} — ${c.empresas} empresas`,
                }))}
              />
              <Campo
                rotulo="Buscar"
                type="search"
                placeholder="Razão social ou CNPJ"
                className="lg:w-72"
                value={busca}
                onChange={(ev) => setBusca(ev.target.value)}
              />
              <FiltroDaProspeccao escolhas={filtro} aoMudar={setFiltro} className="lg:w-[28rem]" />
            </div>

            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-conteudo-sutil">
              <span>
                {coleta.coletadaEm === null
                  ? 'Data da coleta não informada'
                  : `Coletada em ${new Date(`${coleta.coletadaEm}T12:00:00`).toLocaleDateString('pt-BR')}`}
                {coleta.origem === 'ROBO'
                  ? ' pelo robô do Deca'
                  : `${coleta.arquivo === null ? '' : ` · planilha "${coleta.arquivo}"`} · importada em ${new Date(coleta.importadaEm).toLocaleDateString('pt-BR')}`}
              </span>
              <button
                type="button"
                className="text-erro underline-offset-2 hover:underline disabled:opacity-60"
                disabled={apagar.isPending}
                onClick={async () => {
                  const ok = await dialogo.confirmar({
                    titulo: `Excluir a coleta de ${rotuloDoMes(coleta.ano, coleta.mes)}?`,
                    descricao:
                      'Sai a lista deste mês. As anotações das empresas (contatos, deságio, atualizações) ficam, e os cards no CRM também.',
                    rotuloConfirmar: 'Excluir coleta',
                    tom: 'perigo',
                  });
                  if (ok) apagar.mutate(coleta);
                }}
              >
                Excluir esta coleta
              </button>
            </p>
            {apagar.isError && <ErroDaApi titulo="Não foi possível excluir" erro={apagar.error} />}

            {lista.data !== undefined && (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <CartaoIndicador
                  rotulo="Empresas no mês"
                  valor={<span className="tabular-nums">{lista.data.resumo.empresas}</span>}
                />
                <CartaoIndicador
                  rotulo="Crédito somado"
                  valor={
                    <span className="tabular-nums">
                      {formatarMoeda(lista.data.resumo.creditoSomado)}
                    </span>
                  }
                />
                <CartaoIndicador
                  rotulo="Novas no mês"
                  valor={<span className="tabular-nums">{lista.data.resumo.novas}</span>}
                  detalhe={
                    temMesAnterior
                      ? 'Não estavam na coleta anterior.'
                      : 'Primeira coleta: não há mês anterior para comparar.'
                  }
                />
                <CartaoIndicador
                  rotulo="No CRM"
                  valor={<span className="tabular-nums">{lista.data.resumo.noCrm}</span>}
                  detalhe="Com card no pipeline."
                />
              </div>
            )}

            {resultadoDoCard !== null && (
              <AvisoDoCard resultado={resultadoDoCard} aoFechar={() => setResultadoDoCard(null)} />
            )}
            <AvisoDoLote
              desfecho={levarMarcadas.desfecho}
              feitos={(n) => (n === 1 ? '1 card criado no CRM' : `${n} cards criados no CRM`)}
              aoFechar={levarMarcadas.limparDesfecho}
            />

            {lista.isError ? (
              <EstadoErro
                titulo="Não foi possível carregar as empresas"
                mensagem={lista.error.message}
                aoTentarNovamente={() => void lista.refetch()}
              />
            ) : (
              <Tabela
                legenda={`Empresas com crédito no SISCRED — ${rotuloDoMes(coleta.ano, coleta.mes)}`}
                colunas={colunas}
                dados={itens}
                chaveDaLinha={(e) => e.id}
                chaveDeCabecalhoDaLinha="empresa"
                carregando={lista.isPending}
                ordenadaPor={ordenacao.chave}
                direcao={ordenacao.direcao}
                aoOrdenar={(chave) =>
                  setOrdenacao((atual) => ({
                    chave,
                    direcao:
                      atual.chave === chave
                        ? atual.direcao === 'crescente'
                          ? 'decrescente'
                          : 'crescente'
                        : chave === 'empresa'
                          ? 'crescente'
                          : 'decrescente',
                  }))
                }
                aoClicarNaLinha={(e) => setAbertaId(e.id)}
                vazio={
                  <EstadoVazio
                    compacto
                    titulo="Nenhuma empresa com estes filtros"
                    descricao="Limpe a busca ou o filtro para ver o mês inteiro."
                  />
                }
                selecao={{
                  substantivo: { singular: 'empresa', plural: 'empresas', feminino: true },
                  nomeDaLinha: (e) => e.razaoSocial,
                  bloqueio: (e) => (e.card === null ? null : 'Já tem card no CRM.'),
                  acoes: (marcadas, limpar) => (
                    <BotaoDoLote
                      rotulo="Criar card"
                      marcadas={marcadas}
                      limpar={limpar}
                      lote={levarMarcadas}
                      confirmacao={(m) => ({
                        titulo:
                          m.length === 1
                            ? `Criar o card de ${m[0]!.razaoSocial}?`
                            : `Criar card para ${m.length} empresas?`,
                        descricao: `${resumirNomes(m.map((e) => e.razaoSocial))}.\n\nCada uma entra no pipeline como no "Criar card" da linha: a que ainda não tem cadastro no Deca é cadastrada, e os contatos anotados vão junto.`,
                        rotuloConfirmar: 'Criar cards',
                      })}
                    />
                  ),
                }}
              />
            )}
          </div>
        )}
      </div>

      {importando && (
        <ImportarColeta
          aoFechar={() => setImportando(false)}
          aoImportar={(c) => {
            setImportando(false);
            escolherMes(chaveDoMes(c));
          }}
        />
      )}
      {aberta !== null && (
        <AnotacoesDaEmpresa
          empresa={aberta}
          aoFechar={() => setAbertaId(null)}
          aoCriarCard={() => {
            setAbertaId(null);
            criarCard(aberta);
          }}
        />
      )}
    </Pagina>
  );
}

/**
 * O total em destaque e, abaixo, cada modalidade que a empresa tem. Uma coluna
 * por modalidade (como na planilha) não cabia numa tela de notebook junto com
 * contato e CRM — e o "Criar card" ficava fora da vista. A planilha exportada
 * continua com as colunas separadas.
 *
 * Com o filtro de tipo de crédito ligado, o destaque é a soma dos saldos do
 * filtro — o que ordena a lista —, e o total vai para baixo.
 */
function CreditoDaEmpresa(props: {
  readonly empresa: EmpresaProspectada;
  /** Os tipos de crédito marcados no filtro: o destaque passa a ser a soma deles. */
  readonly noFiltro?: readonly Modalidade[];
}): JSX.Element {
  const { saldos, totalCredito } = props.empresa;
  const destaque =
    props.noFiltro === undefined ? totalCredito : creditoNoFiltro(saldos, props.noFiltro);
  return (
    <span className="flex flex-col items-end gap-0.5">
      <span className="font-semibold text-conteudo-primario">{formatarMoeda(destaque)}</span>
      {MODALIDADES.map((m) => {
        const v = totalDaModalidade(saldos, m.chave);
        return v === 0 ? null : (
          <span key={m.chave} className="text-xs text-conteudo-sutil">
            {m.rotulo} {formatarMoeda(v)}
          </span>
        );
      })}
      {props.noFiltro !== undefined && (
        <span className="text-xs text-conteudo-sutil">Total {formatarMoeda(totalCredito)}</span>
      )}
    </span>
  );
}

/**
 * Para quem ligar: o primeiro contato anotado, com o telefone e o e-mail dele
 * — ou, se ele não tiver, os do extrato (ligar na empresa e pedir por ele).
 * Sem contato anotado, o extrato. Os outros contatos viram contagem.
 */
function ResumoDoContato(props: { readonly empresa: EmpresaProspectada }): JSX.Element {
  const { telefone, email, anotacao } = props.empresa;
  const [primeiro, ...outros] = anotacao.contatos;
  const nome =
    primeiro === undefined
      ? ''
      : [primeiro.responsavel, primeiro.cargo].filter(Boolean).join(' — ');
  const fone = primeiro?.telefone ?? telefone;
  const mail = primeiro?.email ?? email;
  if (nome === '' && fone === null && mail === null) {
    return <span className="text-conteudo-sutil">—</span>;
  }
  return (
    <span className="flex min-w-0 flex-col gap-0.5 text-sm">
      {nome !== '' && <span className="font-medium text-conteudo-primario">{nome}</span>}
      {fone !== null && <span className="tabular-nums">{fone}</span>}
      {mail !== null && <span className="truncate text-conteudo-secundario">{mail}</span>}
      {outros.length > 0 && (
        <span className="text-xs text-conteudo-sutil">
          +{outros.length} {outros.length === 1 ? 'contato' : 'contatos'}
        </span>
      )}
    </span>
  );
}

function AvisoDoCard(props: {
  readonly resultado: ResultadoDoCard;
  readonly aoFechar: () => void;
}): JSX.Element {
  const r = props.resultado;
  if ('erro' in r) {
    return (
      <div className="flex flex-col gap-2">
        <ErroDaApi titulo={`Não foi possível criar o card de ${r.razaoSocial}`} erro={r.erro} />
        <button
          type="button"
          onClick={props.aoFechar}
          className="self-start text-xs text-conteudo-sutil underline-offset-2 hover:underline"
        >
          Dispensar
        </button>
      </div>
    );
  }
  return (
    <Aviso
      tom="positivo"
      titulo={
        r.cardJaExistia
          ? `${r.razaoSocial} já estava no pipeline`
          : `${r.razaoSocial} foi para o pipeline`
      }
      aoFechar={props.aoFechar}
      acao={
        <Link
          to={`/crm/pipeline?card=${r.cardId}`}
          className="text-sm font-medium underline underline-offset-2"
        >
          Abrir card
        </Link>
      }
    >
      {r.cardJaExistia
        ? 'O card já existia.'
        : r.empresaCadastrada
          ? 'A empresa não estava no cadastro: foi cadastrada com os dados do SISCRED (CNPJ, razão social, e-mail, telefone, CAD/ICMS e credencial). Complete o endereço em Empresas quando precisar.'
          : 'A empresa já estava no cadastro; o card entrou na primeira coluna.'}
      {r.contatosLevados > 0 &&
        ` ${r.contatosLevados === 1 ? 'O contato anotado foi' : `Os ${r.contatosLevados} contatos anotados foram`} junto: estão no card, em Colaboradores, como contatos sem acesso ao Deca.`}
    </Aviso>
  );
}

/** Por nome, ou pelo crédito — o dos tipos marcados no filtro, se há; senão o total. */
function ordenar(
  itens: readonly EmpresaProspectada[],
  { chave, direcao }: Ordenacao,
  modalidades: readonly Modalidade[],
): EmpresaProspectada[] {
  const sinal = direcao === 'crescente' ? 1 : -1;
  const credito = (e: EmpresaProspectada): number =>
    modalidades.length > 0 ? creditoNoFiltro(e.saldos, modalidades) : e.totalCredito;
  return [...itens].sort(
    (a, b) =>
      sinal *
      (chave === 'empresa'
        ? a.razaoSocial.localeCompare(b.razaoSocial, 'pt-BR')
        : credito(a) - credito(b)),
  );
}
